import React, { useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import {
  CheckCircle2,
  Clock,
  Copy,
  FileText,
  ImageIcon,
  Share2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import type { FormattedTransaction } from "@/interfaces/transaction";
import { toast } from "sonner";
import { formatDate, formatEvmHumanBalance } from "@/utils";
import { Separator } from "../ui/separator";
import { cn } from "@/lib/utils";

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  selectedTx: FormattedTransaction | null;
}

const StatusPill = ({ status }: { status: string }) => {
  const styles = {
    COMPLETED:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    PENDING:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    FAILED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };

  const icons = {
    COMPLETED: CheckCircle2,
    PENDING: Clock,
    FAILED: XCircle,
  };

  const Icon = icons[status as keyof typeof icons] ?? ShieldCheck;

  return (
    <div
      className={`flex flex-col items-center justify-center gap-1 rounded-md px-5 py-1 text-xs font-medium leading-none ${
        styles[status as keyof typeof styles] ??
        "bg-muted text-muted-foreground"
      }`}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
    </div>
  );
};

const TransactionReceipt = ({
  isModalOpen,
  setIsModalOpen,
  selectedTx,
}: Props) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  console.log("TXN", selectedTx);

  if (!selectedTx) return null;

  const exportCanvas = async () => {
    if (!receiptRef.current) return null;

    const html2canvas = (await import("html2canvas")).default;

    return html2canvas(receiptRef.current, {
      scale: 3,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
  };

  const saveAsImage = async () => {
    try {
      const canvas = await exportCanvas();

      if (!canvas) return;

      const link = document.createElement("a");

      link.download = `receipt-${selectedTx.id.slice(0, 8)}.png`;
      link.href = canvas.toDataURL("image/png");

      link.click();

      toast.success("Receipt saved");
    } catch (error) {
      console.error(error);

      toast.error("Unable to save receipt");
    }
  };

  const saveAsPDF = async () => {
    try {
      const canvas = await exportCanvas();

      if (!canvas) return;

      const jsPDF = (await import("jspdf")).default;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const image = canvas.toDataURL("image/png");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const ratio = Math.min(
        pageWidth / canvas.width,
        pageHeight / canvas.height,
      );

      const width = canvas.width * ratio;
      const height = canvas.height * ratio;

      pdf.addImage(image, "PNG", (pageWidth - width) / 2, 10, width, height);

      pdf.save(`receipt-${selectedTx.id.slice(0, 8)}.pdf`);

      toast.success("PDF downloaded");
    } catch (error) {
      console.error(error);

      toast.error("Unable to generate PDF");
    }
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);

      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareReceipt = async () => {
    const text = `BuyFiat Receipt Amount: ${selectedTx.amount.formatted} ${selectedTx.asset.symbol}
Transaction ID:${selectedTx.id}

Date:${formatDate(selectedTx.timestamps.createdAt)}

Status:${selectedTx.status}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Transaction Receipt",
          text,
        });

        toast.success("Receipt shared");
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          await copyToClipboard(text, "Receipt");
        }
      }
    } else {
      await copyToClipboard(text, "Receipt");
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="flex h-[90dvh] max-h-[90dvh] w-[calc(100%-1rem)] max-w-md flex-col overflow-hidden rounded-2xl p-0 sm:max-w-lg">
        {/* Receipt */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div
            ref={receiptRef}
            className="relative overflow-hidden bg-background text-foreground"
          >
            {/* Watermark */}
            {/* 1ST WATER MARK*/}
            {/* <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
          >
            <span className=" select-none whitespace-nowrap text-[5rem] sm:text-[7rem] font-extrabold tracking-[1rem] text-primary/5 -rotate-45 uppercase">
              BUYFIAT
            </span>
          </div> */}

            {/* 2ND WATER MARK */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none overflow-hidden -rotate-45 opacity-[0.04]"
            >
              <div className="flex flex-col gap-12">
                {Array.from({ length: 6 }).map((_, i) => (
                  <p key={i} className="text-7xl font-black tracking-[1rem]">
                    BUYFIAT BUYFIAT BUYFIAT
                  </p>
                ))}
              </div>
            </div>

            {/* 3RD WATER MARK */}

            {/* <div
            aria-hidden="true"
            className="absolute -bottom-16 -left-24 pointer-events-none select-none -rotate-45 text-[8rem] font-black tracking-[1rem] uppercase text-primary/5 whitespace-nowrap"
          >
            BUYFIAT
          </div> */}
            <div className="relative z-10">
              {/* Header */}

              <div className="border-b px-6 py-6">
                <div className="flex items-center justify-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-lg text-center font-bold text-primary-foreground">
                    B
                  </div>

                  <div className="flex flex-col">
                    <DialogTitle className="text-xl font-semibold">
                      BuyFiat
                    </DialogTitle>

                    <p className="text-sm text-muted-foreground">
                      Digital Transaction Receipt
                    </p>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="px-6 py-8 flex flex-col items-center justify-center text-center">
                <div className="flex items-center gap-2 text-4xl sm:text-5xl font-bold ">
                  <h1 className="tracking-tight ">
                    {formatEvmHumanBalance(
                      Number(selectedTx?.amount?.value),
                      selectedTx?.asset?.token?.isNative,
                    )}
                  </h1>

                  <p className="font-medium text-primary">
                    {selectedTx?.asset?.symbol}
                  </p>
                </div>

                <div className="mt-5 flex items-center justify-center">
                  <StatusPill status={selectedTx?.status} />
                </div>
              </div>

              <Separator />

              {/* Details */}
              <div className="space-y-1 px-6 py-6">
                <DetailRow
                  label="Transaction ID"
                  value={selectedTx.id}
                  mono
                  // copyable
                  // onCopy={() =>
                  //   copyToClipboard(selectedTx.id, "Transaction ID")
                  // }
                />

                <DetailRow
                  label="Date"
                  value={formatDate(selectedTx?.timestamps?.createdAt)}
                />

                <DetailRow
                  label="Sender"
                  value={selectedTx?.addresses?.from}
                  mono
                  // copyable
                  // onCopy={() =>
                  //   copyToClipboard(
                  //     selectedTx?.addresses?.from,
                  //     "Sender address",
                  //   )
                  // }
                />

                <DetailRow
                  label="Recipient"
                  value={selectedTx?.addresses?.to}
                  mono
                  // copyable
                  // onCopy={() =>
                  //   copyToClipboard(
                  //     selectedTx?.addresses.to,
                  //     "Recipient address",
                  //   )
                  // }
                />
                <DetailRow
                  label="Network"
                  value={selectedTx?.asset?.network?.name}
                  // copyable
                  // onCopy={() =>
                  //   copyToClipboard(
                  //     selectedTx?.addresses.to,
                  //     "Recipient address",
                  //   )
                  // }
                />

                <DetailRow
                  label="Counterparty"
                  value={selectedTx?.counterparty?.name}
                />

                {/* <DetailRow
              label="Fee"
              value={`₦${selectedTx.metadata?.fee ?? "0.00"}`}
            /> */}

                <DetailRow
                  label="Amount"
                  value={` ${selectedTx?.asset?.token?.code} ${formatEvmHumanBalance(
                    selectedTx?.amount?.value,
                    selectedTx?.asset?.token?.isNative,
                  )}`}
                  highlight
                />
              </div>

              <Separator />

              {/* Verification */}
              <div className="px-6 py-6">
                <div className="flex flex-col items-center justify-center gap-2 text-sm font-medium">
                  <ShieldCheck className="h-10 w-10 text-primary" />
                  Verified by BuyFiat Ledger
                </div>

                <p className="mt-2 text-center text-xs text-muted-foreground">
                  This receipt confirms that the transaction has been securely
                  recorded on the BuyFiat platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}

        <div className="grid grid-cols-3 gap-2 border-t bg-muted/30 p-4">
          <Button
            variant="outline"
            onClick={saveAsImage}
            className="flex items-center gap-2"
          >
            <ImageIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Image</span>
          </Button>

          <Button
            variant="outline"
            onClick={saveAsPDF}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">PDF</span>
          </Button>

          <Button onClick={shareReceipt} className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DetailRow = ({
  label,
  value,
  mono = false,
  copyable = false,
  highlight = false,
  onCopy,
}: {
  label: string;
  value: string | React.ReactNode;
  mono?: boolean;
  copyable?: boolean;
  highlight?: boolean;
  onCopy?: () => void;
}) => {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4 border-b py-3 last:border-none",
      )}
    >
      {/* Label */}
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>

      {/* Value */}
      <div className="flex min-w-0 items-center justify-end gap-2">
        <span
          className={`text-right text-sm break-all ${mono ? "font-mono text-xs text-muted-foreground" : ""} ${highlight ? "font-semibold text-emerald-500 text-xl" : ""}`}
          title={typeof value === "string" ? value : undefined}
        >
          {value || "—"}
        </span>

        {copyable && onCopy && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCopy}
            className="h-7 w-7 shrink-0"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default TransactionReceipt;
